import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Autocomplete, TextField, Chip, ListItem, Popper } from "@mui/material";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  useController,
  useFormContext,
} from "react-hook-form";
import { getContrastColor } from "@/modules/common/utils";

export interface Option {
  value: string | number;
  label: string;
  color?: string;
  new_color?: string;
  rank?: string;
  searchKey?: string;
}

interface SingleSelectChipProps<T extends FieldValues> {
  name: Path<T>;
  control?: Control<T>;
  options: Option[];
  label: string;
  error?: string;
  onChange?: (value: string | number) => void;
  onInputChange?: (value: string) => void;
  size?: "small" | "medium";
  value?: string | number | null;
  autoFocus?: boolean;
  zoomLevel?: number;
  fullwidth?: boolean;
  errorText?: string;
  disabled?: boolean;
  defaultFlag?: boolean;
  noOptionsText?: string;
  testId?: string;
}

export const SingleAutoCompleteSelectChip = <T extends FieldValues>({
  name,
  control,
  options,
  label,
  error,
  onChange,
  onInputChange,
  value,
  size = "medium",
  autoFocus,
  zoomLevel,
  fullwidth,
  errorText,
  disabled,
  defaultFlag = true,
  testId,
}: SingleSelectChipProps<T>) => {
  const formContext = useFormContext<T>();
  const [isOpen, setIsOpen] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target as Node) &&
        !document
          .querySelector(".MuiAutocomplete-popper")
          ?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const uniqueOptions = useMemo(() => {
    const uniqueValues = new Set();
    const unique: Option[] = [];
    const redundant: Option[] = [];

    options.forEach((option) => {
      if (!uniqueValues.has(option.value)) {
        uniqueValues.add(option.value);
        unique.push(option);
      } else {
        redundant.push(option);
      }
    });

    return unique;
  }, [options]);

  const findOptionByValue = (value: string | number | undefined | null) =>
    uniqueOptions.find((option) => option.value === value) || null;

  const [localValue, setLocalValue] = useState<Option | null>(
    findOptionByValue(value)
  );

  useEffect(() => {
    const newValue = findOptionByValue(value);
    if (JSON.stringify(newValue) !== JSON.stringify(localValue)) {
      setLocalValue(newValue);
    }
  }, [value, uniqueOptions, localValue]);

  const handleChange = (newValue: Option | null) => {
    setLocalValue(newValue);
    if (newValue) {
      onChange && onChange(newValue.value);
    } else {
      onChange && onChange("");
    }
  };

  const renderUserOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: Option) => (
      <ListItem
        {...props}
        key={option.value}
        sx={{
          textTransform: "capitalize",
          backgroundColor: option.new_color || option.color || "#FFF",
          color: getContrastColor(option.new_color || option.color || "#FFF"),
          padding: "5px 10px",
          "&:hover": {
            backgroundColor: `color-mix(in srgb, ${
              option.new_color || option.color || "#FFF"
            } 80%, black) !important`,
          },
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div>{option.label}</div>
          {option.rank !== undefined && (
            <div style={{ fontSize: "0.8em", opacity: 0.7 }}>{option.rank}</div>
          )}
        </div>
      </ListItem>
    ),
    []
  );

  const renderAutocomplete = (field: any) => (
    <div ref={autocompleteRef} data-testid={testId}>
      <Autocomplete
        {...(defaultFlag
          ? { defaultValue: localValue }
          : { value: localValue })}
        options={uniqueOptions}
        loading={disabled}
        loadingText="Loading..."
        filterOptions={(options, { inputValue }) =>
          options.filter((option) =>
            option?.searchKey
              ? option.searchKey
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              : option.label
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
          )
        }
        autoFocus={autoFocus}
        defaultValue={localValue}
        value={localValue}
        getOptionLabel={(option: Option) => option.label}
        onChange={(_, newValue: Option | null) => {
          handleChange(newValue);
          field.onChange(newValue ? newValue.value : null);
        }}
        onInputChange={(_, value, reason) => {
          if (reason === "input") {
            onInputChange?.(value);
          }
        }}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={(_, reason) => {
          if (reason === "selectOption" || reason === "escape") {
            setIsOpen(false);
          }
        }}
        blurOnSelect={false}
        fullWidth={fullwidth}
        disabled={disabled}
        sx={{
          "& .MuiAutocomplete-popper": {
            zoom: zoomLevel ? `${zoomLevel}%` : 1,
            position: "fixed !important",
            zIndex: 1000,
          },
          "& .MuiAutocomplete-listbox": {
            maxHeight: "250px",
          },
        }}
        PopperComponent={({ style, ...props }) => (
          <Popper
            {...props}
            style={{
              ...style,
              position: "fixed",
            }}
            placement="bottom-start"
            modifiers={[
              {
                name: "preventOverflow",
                enabled: true,
                options: {
                  altAxis: true,
                  boundary: "viewport",
                },
              },
            ]}
          />
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size={size}
            label={label}
            error={!!error}
            helperText={errorText || error}
            focused={autoFocus}
            autoFocus={autoFocus}
          />
        )}
        renderOption={(props, option) =>
          renderUserOption(props, option as Option)
        }
        renderTags={(value: Option[], getTagProps) =>
          value.map((option: Option, index) => (
            <Chip
              label={option.label}
              {...getTagProps({ index })}
              key={option.value}
              color="primary"
              variant="outlined"
              style={{
                backgroundColor: option?.new_color || option?.color || "",
                color: getContrastColor(
                  option?.new_color || option?.color || "#e0e0e0"
                ),
              }}
            />
          ))
        }
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
      />
    </div>
  );

  if (control) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) =>
          renderAutocomplete({
            ...field,
            value: findOptionByValue(field.value),
          })
        }
      />
    );
  } else if (formContext) {
    try {
      const { field } = useController({ name, control: formContext.control });
      return renderAutocomplete({
        ...field,
        value: findOptionByValue(field.value),
      });
    } catch (error) {
      console.error("Error in SingleAutoCompleteSelectChip:", error);
      return renderAutocomplete({ value: null, onChange: () => {} });
    }
  } else {
    console.warn(
      "SingleAutoCompleteSelectChip: No form context found. Rendering uncontrolled component."
    );
    return renderAutocomplete({ value: null, onChange: () => {} });
  }
};
